<template>
  <div class="chunked-uploader">
    <!-- Upload Mode Toggle -->
    <div class="upload-mode-toggle mb-4">
      <div class="flex space-x-2">
        <button 
          @click="uploadMode = 'files'"
          :class="[
            'px-4 py-2 rounded text-sm font-medium',
            uploadMode === 'files' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          ]"
        >
          Dosyalar
        </button>
        <button 
          @click="uploadMode = 'folders'"
          :class="[
            'px-4 py-2 rounded text-sm font-medium transition-all duration-300',
            uploadMode === 'folders' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
            highlightKlasorler ? 'ring-2 ring-blue-400 ring-opacity-75 animate-pulse' : ''
          ]"
        >
          Klasörler
        </button>
      </div>
    </div>

    <!-- Folder Detection Warning -->
    <div v-if="showFolderWarning" class="folder-warning mb-4">
      <div class="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div class="flex items-center">
          <i class="fas fa-exclamation-triangle text-amber-600 mr-2"></i>
          <span class="text-sm font-medium text-amber-800">
            Klasör algılandı! Klasörler moduna geçiliyor...
          </span>
        </div>
      </div>
    </div>

    <!-- Drop Zone -->
    <div 
      class="drop-zone"
      :class="{ 'drag-over': isDragOver }"
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @click="openFileDialog"
    >
      <div class="drop-zone-content">
        <i class="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-4"></i>
        <h3 class="text-lg font-medium text-gray-700 mb-2">
          {{ uploadMode === 'files' ? 'Dosyaları buraya bırakın' : 'Klasörleri buraya bırakın' }}
        </h3>
        <p class="text-sm text-gray-500 mb-4">veya göz atmak için tıklayın</p>
        <button class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
          {{ uploadMode === 'files' ? 'Dosya Seç' : 'Klasör Seç' }}
        </button>
      </div>
      <input 
        ref="fileInput"
        type="file" 
        :multiple="uploadMode === 'files'"
        :webkitdirectory="uploadMode === 'folders'"
        class="hidden"
        @change="handleFileSelect"
      />
    </div>

    <!-- Upload Status Summary -->
    <div v-if="uploads.length > 0" class="uploads-summary mt-4">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="fas fa-cloud-upload-alt text-blue-600 mr-2"></i>
            <span class="text-sm font-medium text-blue-800">
              {{ uploads.length }} dosya yükleniyor
            </span>
          </div>
          <div class="text-xs text-blue-600">
            Dosyalar ana listede görünecek
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineEmits } from 'vue';
import { ChunkedUploadService, type ChunkedUploadOptions } from '../services/chunkedUploadService';
import { ApiService } from '../services/apiService';
import { useFileStore } from '../stores/fileStore';
import type { FileItem, UploadProgress } from '../types/api';

interface Props {
  appId: string;
  folderId: string | null;
}

interface UploadItem {
  uploadId: string;
  fileName: string;
  fileSize: number;
  percentage: number;
  status: 'uploading' | 'paused' | 'completed' | 'error';
  speed?: number;
  timeRemaining?: number;
}

const props = withDefaults(defineProps<Props>(), {
  folderId: undefined
});

const emit = defineEmits<{
  'upload-complete': [file: FileItem];
  'error': [message: string];
}>();

const isDragOver = ref(false);
const fileInput = ref<HTMLInputElement>();
const uploadMode = ref<'files' | 'folders'>('files');
const showFolderWarning = ref(false);
const highlightKlasorler = ref(false);
const apiService = new ApiService();
const fileStore = useFileStore();

// Use store's upload progress and convert to UploadItem format for UI
const uploads = computed<UploadItem[]>(() => {
  return Object.entries(fileStore.uploadProgress).map(([uploadId, progress]) => {
    // Extract filename from uploadId (format: filename-timestamp)
    const lastDashIndex = uploadId.lastIndexOf('-');
    const fileName = lastDashIndex > 0 ? uploadId.substring(0, lastDashIndex) : uploadId;
    
    return {
      uploadId,
      fileName: fileName.replace(/_/g, ' '), // Restore spaces in filename
      fileSize: 0, // File size not available in progress data
      percentage: Math.round(progress.percentage),
      status: 'uploading' as const,
      speed: progress.speed,
      timeRemaining: progress.timeRemaining
    };
  });
});

const openFileDialog = () => {
  fileInput.value?.click();
};

const detectFolderInItems = (items: DataTransferItem[]): boolean => {
  return items.some(item => {
    const entry = item.webkitGetAsEntry();
    return entry && entry.isDirectory;
  });
};

const detectFolderInFiles = (files: File[]): boolean => {
  return files.some(file => (file as any).webkitRelativePath);
};

const showWarningAndSwitch = () => {
  showFolderWarning.value = true;
  uploadMode.value = 'folders';
  highlightKlasorler.value = true;
  
  // Hide warning after 3 seconds
  setTimeout(() => {
    showFolderWarning.value = false;
  }, 3000);
  
  // Remove highlighting after 1 second
  setTimeout(() => {
    highlightKlasorler.value = false;
  }, 1000);
};

const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = Array.from(target.files || []);
  
  // Check if trying to upload folders in files mode
  if (uploadMode.value === 'files' && detectFolderInFiles(files)) {
    showWarningAndSwitch();
    // Continue processing as folder selection
    await handleFolderSelect(files);
    target.value = ''; // Reset input
    return;
  }
  
  if (uploadMode.value === 'files') {
    startUploads(files);
  } else {
    // Handle folder selection
    await handleFolderSelect(files);
  }
  
  target.value = ''; // Reset input
};

const handleDrop = async (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;
  
  const items = Array.from(event.dataTransfer?.items || []);
  
  // Check if trying to upload folders in files mode
  if (uploadMode.value === 'files' && detectFolderInItems(items)) {
    showWarningAndSwitch();
    // Continue processing as folder drop
    await handleFolderDrop(items);
    return;
  }
  
  if (uploadMode.value === 'files') {
    const files = Array.from(event.dataTransfer?.files || []);
    startUploads(files);
  } else {
    // Handle folder drop
    await handleFolderDrop(items);
  }
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = () => {
  isDragOver.value = false;
};



const startUploads = async (files: File[]) => {
  try {
    const uploadedFiles = await fileStore.uploadFiles(files, props.appId, {
      folderId: props.folderId,
      onProgress: (_progress: UploadProgress) => {
        // Progress is automatically tracked in the store
      },
      onError: (error) => {
        emit('error', `Upload failed: ${error.error}`);
      }
    });
    
    // Emit completion for each file
    uploadedFiles.forEach(file => {
      emit('upload-complete', file);
    });
    
  } catch (error) {
    console.error('Upload failed:', error);
    emit('error', 'Dosya yüklemesi başarısız oldu');
  }
};

const cancelUpload = (uploadId: string) => {
  // Remove from store's upload progress and uploading files
  if (fileStore.uploadProgress[uploadId]) {
    delete fileStore.uploadProgress[uploadId];
  }
  fileStore.removeUploadingFile(uploadId);
};

const formatFileSize = (bytes: number): string => {
  return ChunkedUploadService.formatFileSize(bytes);
};

const formatSpeed = (bytesPerSecond: number): string => {
  return ChunkedUploadService.formatSpeed(bytesPerSecond);
};

// Folder handling functions
const handleFolderSelect = async (files: File[]) => {
  const folderStructure = await buildFolderStructure(files);
  await processFolderStructure(folderStructure);
};

const handleFolderDrop = async (items: DataTransferItem[]) => {
  const folderStructure = await buildFolderStructureFromItems(items);
  await processFolderStructure(folderStructure);
};

interface FolderStructure {
  [folderPath: string]: {
    folderId?: string;
    files: File[];
    parent?: string;
  };
}

const buildFolderStructure = async (files: File[]): Promise<FolderStructure> => {
  const structure: FolderStructure = {};
  
  for (const file of files) {
    const relativePath = (file as any).webkitRelativePath || file.name;
    const pathParts = relativePath.split('/');
    
    if (pathParts.length > 1) {
      // This file is in a folder structure
      const folderPath = pathParts.slice(0, -1).join('/');
      
      if (!structure[folderPath]) {
        structure[folderPath] = {
          files: [],
          parent: pathParts.length > 2 ? pathParts.slice(0, -2).join('/') : undefined
        };
      }
      
      structure[folderPath].files.push(file);
    } else {
      // File in root
      if (!structure['']) {
        structure[''] = { files: [] };
      }
      structure[''].files.push(file);
    }
  }
  
  return structure;
};

const buildFolderStructureFromItems = async (items: DataTransferItem[]): Promise<FolderStructure> => {
  const structure: FolderStructure = {};
  
  const processEntry = async (entry: any, path: string = '') => {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve) => {
        entry.file((file: File) => {
          resolve(file);
        });
      });
      
      const folderPath = path;
      if (!structure[folderPath]) {
        structure[folderPath] = {
          files: [],
          parent: folderPath.includes('/') ? folderPath.split('/').slice(0, -1).join('/') : undefined
        };
      }
      structure[folderPath].files.push(file);
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const entries = await new Promise<any[]>((resolve) => {
        reader.readEntries((entries: any[]) => {
          resolve(entries);
        });
      });
      
      const currentPath = path ? `${path}/${entry.name}` : entry.name;
      
      for (const childEntry of entries) {
        await processEntry(childEntry, currentPath);
      }
    }
  };
  
  for (const item of items) {
    const entry = item.webkitGetAsEntry();
    if (entry) {
      await processEntry(entry);
    }
  }
  
  return structure;
};

const processFolderStructure = async (structure: FolderStructure) => {
  try {
    // Create folders first, in order (parent before children)
    const folderPaths = Object.keys(structure)
      .filter(path => path !== '')
      .sort((a, b) => a.split('/').length - b.split('/').length);
    
    for (const folderPath of folderPaths) {
      const folderInfo = structure[folderPath];
      const pathParts = folderPath.split('/');
      const folderName = pathParts[pathParts.length - 1];
      
      let parentId = props.folderId || undefined;
      if (folderInfo.parent) {
        parentId = structure[folderInfo.parent]?.folderId;
      }
      
      // Create folder via API
      try {
        const folder = await fileStore.createFolder(props.appId, {
          name: folderName,
          parentId
        });
        folderInfo.folderId = folder.id;
      } catch (error) {
        console.error('Error creating folder:', error);
      }
    }
    
    // Now upload files to their respective folders
    for (const [folderPath, folderInfo] of Object.entries(structure)) {
      if (folderInfo.files.length > 0) {
        const targetFolderId = folderPath === '' ? props.folderId : folderInfo.folderId;
        startUploadsToFolder(folderInfo.files, targetFolderId);
      }
    }
  } catch (error) {
    console.error('Error processing folder structure:', error);
    emit('error', 'Klasör yapısı işlenirken hata oluştu');
  }
};

const startUploadsToFolder = async (files: File[], targetFolderId: string | null | undefined) => {
  try {
    const uploadedFiles = await fileStore.uploadFiles(files, props.appId, {
      folderId: targetFolderId || null,
      onProgress: (_progress: UploadProgress) => {
        // Progress is automatically tracked in the store
      },
      onError: (error) => {
        emit('error', `Upload failed: ${error.error}`);
      }
    });
    
    // Emit completion for each file
    uploadedFiles.forEach(file => {
      emit('upload-complete', file);
    });
    
  } catch (error) {
    console.error('Upload failed:', error);
    emit('error', 'Dosya yüklemesi başarısız oldu');
  }
};

defineExpose({
  startUploads
});
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.drop-zone:hover,
.drop-zone.drag-over {
  border-color: #3b82f6;
  background-color: #f0f9ff;
}

.upload-item {
  transition: all 0.2s;
}

.upload-item:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

@keyframes highlight {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.animate-pulse {
  animation: highlight 0.5s ease-in-out;
}
</style> 